import { Controller } from "@hotwired/stimulus"

// How far the arrow keys move a scrolling face, and the slice of it a page key moves.
const ARROW_SCROLL_STEP = 48
const PAGE_SCROLL_RATIO = 0.9

// One learner-side flip card: clicking (or Enter/Space — the card is a button)
// rotates the card between its front and back faces. The face turned away is
// hidden from assistive tech too, so the button's accessible name always matches
// the side the learner can actually see.
//
// The card holds a fixed size, so a face carrying more text than fits scrolls inside it
// instead of stretching it. That scroll area cannot be focusable — it lives inside the
// card's own button — so the arrow keys are handled here while the card has focus, and
// the fade, the caret and the spoken hint are fitted only to a face that overflows.
export default class extends Controller {
  static targets = ["front", "back"]
  static values = { hintId: String }

  connect() {
    this.boundMarkScrollPosition = (event) => this.markScrollPosition(event.target)
    this.scrollAreas.forEach((area) => area.addEventListener("scroll", this.boundMarkScrollPosition))

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.measureFaces())
      this.resizeObserver.observe(this.element)
    }

    this.measureFaces()

    // A late webfont reflows the text without resizing the card, so what fit on connect
    // may not fit once the real face is painted. Turbo can swap the page away first.
    document.fonts?.ready?.then(() => this.element.isConnected && this.measureFaces())
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.scrollAreas.forEach((area) => area.removeEventListener("scroll", this.boundMarkScrollPosition))
  }

  flip() {
    const flipped = this.element.classList.toggle("is-flipped")

    this.element.setAttribute("aria-pressed", flipped ? "true" : "false")
    this.setFaceHidden(this.frontTarget, flipped)
    this.setFaceHidden(this.backTarget, !flipped)
    this.describeOverflow()
  }

  // Enter and Space stay with the button and flip the card; only the reading keys are
  // taken, and only while the face they would move actually has somewhere to move to —
  // otherwise the key belongs to the page.
  scrollFace(event) {
    const area = this.visibleScrollArea
    if (!area) return

    const offset = this.scrollOffsetFor(event.key, area)
    if (offset === null) return

    const target = Math.min(Math.max(area.scrollTop + offset, 0), area.scrollHeight - area.clientHeight)
    if (target === area.scrollTop) return

    event.preventDefault()
    area.scrollTop = target
  }

  scrollOffsetFor(key, area) {
    const page = Math.max(area.clientHeight * PAGE_SCROLL_RATIO, ARROW_SCROLL_STEP)

    switch (key) {
      case "ArrowDown": return ARROW_SCROLL_STEP
      case "ArrowUp": return -ARROW_SCROLL_STEP
      case "PageDown": return page
      case "PageUp": return -page
      case "End": return area.scrollHeight
      case "Home": return -area.scrollHeight
      default: return null
    }
  }

  measureFaces() {
    this.faces.forEach((face) => {
      const area = this.scrollAreaFor(face)
      const overflowing = !!area && area.scrollHeight - area.clientHeight > 1

      face.classList.toggle("has-overflow", overflowing)
      if (area) this.markScrollPosition(area)
    })

    this.describeOverflow()
  }

  // Each edge the face is clipped at wears its own fade, so the line the clip cuts
  // through reads as more text in that direction rather than as a sliced one.
  markScrollPosition(area) {
    const face = area.closest(".academy-flip-card-face")
    if (!face) return

    face.classList.toggle("is-scrolled", area.scrollTop > 0)
    face.classList.toggle("is-at-end", area.scrollTop >= area.scrollHeight - area.clientHeight - 1)
  }

  // The hint describes how to reach the rest of the text, so it is only attached while
  // the side the learner is actually on has text out of reach.
  describeOverflow() {
    if (!this.hasHintIdValue) return

    if (this.visibleFace?.classList.contains("has-overflow")) {
      this.element.setAttribute("aria-describedby", this.hintIdValue)
    } else {
      this.element.removeAttribute("aria-describedby")
    }
  }

  setFaceHidden(face, hidden) {
    if (hidden) {
      face.setAttribute("aria-hidden", "true")
    } else {
      face.removeAttribute("aria-hidden")
    }
  }

  get faces() {
    return [this.frontTarget, this.backTarget]
  }

  get visibleFace() {
    return this.element.classList.contains("is-flipped") ? this.backTarget : this.frontTarget
  }

  get visibleScrollArea() {
    return this.scrollAreaFor(this.visibleFace)
  }

  get scrollAreas() {
    return this.faces.map((face) => this.scrollAreaFor(face)).filter(Boolean)
  }

  scrollAreaFor(face) {
    return face.querySelector("[data-flip-card-scroll]")
  }
}
